import { MailGenerateAdapter } from '@/common/message-generate/adapters/mail-generate.adapter';
import { MessageGenerateComponent, MessageType } from '@/common/message-generate/message-generate.component';
import { MailPreviewResponse } from '@/vindication/vindication.model';
import { ChangeDetectionStrategy, Component, inject, NgZone, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { MailPreviewDialogComponent } from './mail-preview-dialog/mail-preview-dialog.component';
import { InvoiceMailAttachmentsDialogComponent } from './invoice-mail-attachments-dialog/invoice-mail-attachments-dialog.component';
import { AttachmentTooltipPipe } from '../../pipes/attachment-tooltip.pipe';
import { filter } from 'rxjs';

@Component({
	selector: 'app-invoice-mail',
	standalone: true,
	imports: [MessageGenerateComponent, MatTableModule, MatTooltipModule, MatButtonModule, AttachmentTooltipPipe],
	template: `<div class="m-3">
			<app-message-generate
				[itemsIds]="invoicesIds()"
				[type]="MessageType.EMAIL"
				[previewTemplate]="mailPreviewTpl"
				[attachmentsByItem]="attachmentsByItem()"
				[adapter]="mailAdapter"
			></app-message-generate>
		</div>

		<ng-template
			#mailPreviewTpl
			let-items
			let-previewLoader="previewLoader"
			let-attachmentsByItem="attachmentsByItem"
		>
			<table class="fixed-layout" mat-table [dataSource]="items">
				<ng-container matColumnDef="company">
					<th mat-header-cell *matHeaderCellDef>Firma</th>
					<td mat-cell *matCellDef="let element">
						{{ element.company }}
					</td>
				</ng-container>

				<ng-container matColumnDef="mail">
					<th mat-header-cell *matHeaderCellDef>Mail</th>
					<td mat-cell *matCellDef="let element">
						{{ element.mails }}
					</td>
				</ng-container>

				<ng-container matColumnDef="nip">
					<th mat-header-cell *matHeaderCellDef>NIP</th>
					<td mat-cell *matCellDef="let element">{{ element.nip }}</td>
				</ng-container>

				<ng-container matColumnDef="attachments">
					<th mat-header-cell *matHeaderCellDef>Załączniki</th>
					<td mat-cell *matCellDef="let element">
						<div class="d-flex align-items-center gap-2">
							<button
								mat-icon-button
								[matTooltip]="attachmentsByItem | attachmentTooltip: element.invoice"
								type="button"
								(click)="openAttachmentsDialog(element)"
							>
								<i class="fa fa-paperclip"></i>
							</button>
						</div>
					</td>
				</ng-container>

				<ng-container matColumnDef="subject">
					<th mat-header-cell *matHeaderCellDef>Tytuł</th>
					<td mat-cell *matCellDef="let element">
						{{ element.subject }}
					</td>
				</ng-container>

				<ng-container matColumnDef="message">
					<th mat-header-cell *matHeaderCellDef>Wiadomość</th>
					<td mat-cell *matCellDef="let element">
						<button mat-raised-button color="primary" (click)="openPreviewDialog(element)">Podgląd</button>
					</td>
				</ng-container>

				<tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
				<tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
			</table>
		</ng-template> `,
	styleUrl: './invoice-mail.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceMailComponent implements OnInit {
	readonly invoicesIds = signal<number[]>([]);

	readonly MessageType = MessageType;

	readonly displayedColumns: string[] = ['company', 'mail', 'nip', 'attachments', 'subject', 'message'];

	readonly attachmentsByItem = signal<Record<number, File[]>>({});

	readonly mailAdapter = inject(MailGenerateAdapter);

	private readonly activateRoute = inject(ActivatedRoute);
	private readonly dialog = inject(MatDialog);
	private readonly ngZone = inject(NgZone);

	ngOnInit(): void {
		this.activateRoute.queryParams.subscribe((params) => {
			const ids = params['invoices'];

			if (!ids) {
				return;
			}

			this.invoicesIds.set(ids.split(',').map((id: string) => +id));
		});
	}

	openPreviewDialog(element: MailPreviewResponse): void {
		this.dialog.open(MailPreviewDialogComponent, {
			data: element,
		});
	}

	async openAttachmentsDialog(element: MailPreviewResponse): Promise<void> {
		const currentAttachments = this.attachmentsByItem()[element.invoice] ?? [];

		const invoiceMailAttachmentsDialogComponent = await import(
			'./invoice-mail-attachments-dialog/invoice-mail-attachments-dialog.component'
		).then((c) => c.InvoiceMailAttachmentsDialogComponent);

		this.ngZone.run(() => {
			this.dialog
				.open<InvoiceMailAttachmentsDialogComponent>(invoiceMailAttachmentsDialogComponent, {
					data: {
						invoice: element.invoice,
						company: element.company,
						nip: element.nip,
						attachments: currentAttachments,
					},
					width: '500px',
					disableClose: true,
				})
				.afterClosed()
				.pipe(filter((value): value is File[] => value !== undefined))
				.subscribe((files) => {
					const current = this.attachmentsByItem();
					this.attachmentsByItem.set({ ...current, [element.invoice]: files });
				});
		});
	}
}
